import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { PiWarningCircleLight, PiArrowLeftLight, PiMedalLight, PiBookOpenLight, PiBriefcaseLight, PiCalendarCheckLight, PiCheckCircleLight, PiCaretRightLight, PiArrowSquareOutLight, PiFileTextLight, PiGaugeLight, PiStackLight, PiChartLineUpLight, PiCircleNotchLight, PiMapPinLight, PiShieldCheckLight, PiTargetLight, PiThumbsDownLight, PiUserLight } from 'react-icons/pi';
import { candidateAPI, jobAPI } from '../../services/api';
import MatchIntelligencePanel from '../../components/matches/MatchIntelligencePanel';
import { formatCompensationRange } from '../../utils/compensation';

const LocalSkeletonBlock = ({ className = '', style }) => (
  <div 
    className={`relative overflow-hidden bg-slate-200/60 ${className}`} 
    style={style}
  >
    <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/60 to-transparent" />
  </div>
);

const CandidateProfileSkeleton = () => (
  <div className="min-h-screen bg-white p-4 sm:p-6 lg:p-8">
    <style>{`
      @keyframes shimmer {
        100% { transform: translateX(100%); }
      }
      .animate-shimmer {
        animation: shimmer 1.5s infinite;
      }
    `}</style>
    <div className="mx-auto max-w-[1500px] space-y-6">
      <div>
        <nav className="mb-4 flex items-center gap-2">
          <LocalSkeletonBlock className="h-4 w-24 rounded-md" />
          <LocalSkeletonBlock className="h-4 w-4 rounded-md" />
          <LocalSkeletonBlock className="h-4 w-32 rounded-md" />
        </nav>
        <div className="flex flex-col gap-4 rounded-lg border border-slate-200/80 bg-white p-4 shadow-[0_14px_36px_rgba(15,23,42,0.045)] lg:flex-row lg:items-start lg:justify-between">
          <div className="flex min-w-0 items-start gap-4">
            <LocalSkeletonBlock className="h-14 w-14 rounded-lg" />
            <div className="space-y-2">
              <LocalSkeletonBlock className="h-4 w-32 rounded-md" />
              <LocalSkeletonBlock className="h-8 w-64 rounded-md" />
              <div className="flex gap-2 pt-1">
                <LocalSkeletonBlock className="h-4 w-24 rounded-md" />
                <LocalSkeletonBlock className="h-4 w-32 rounded-md" />
                <LocalSkeletonBlock className="h-4 w-20 rounded-md" />
              </div>
            </div>
          </div>
          <div className="flex gap-2">
             <LocalSkeletonBlock className="h-10 w-32 rounded-lg" />
             <LocalSkeletonBlock className="h-10 w-28 rounded-lg" />
             <LocalSkeletonBlock className="h-10 w-20 rounded-lg" />
          </div>
        </div>
      </div>
      
      <section className="rounded-lg border border-slate-200/80 bg-white p-4 shadow-[0_14px_36px_rgba(15,23,42,0.045)]">
         <div className="grid gap-5 xl:grid-cols-[minmax(280px,360px)_minmax(0,1fr)]">
            <LocalSkeletonBlock className="h-80 w-full rounded-lg" />
            <div className="grid gap-4 lg:grid-cols-2">
               <LocalSkeletonBlock className="h-80 w-full rounded-lg" />
               <LocalSkeletonBlock className="h-80 w-full rounded-lg" />
            </div>
         </div>
      </section>
      
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
        {Array.from({ length: 5 }).map((_, i) => (
           <LocalSkeletonBlock key={i} className="h-28 w-full rounded-lg" />
        ))}
      </div>
    </div>
  </div>
);

const labelize = (value) => (
  String(value || 'Not set')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase())
);

const formatNumber = (value) => new Intl.NumberFormat('en-US').format(Number(value) || 0);

const formatMoney = (salary) => formatCompensationRange(salary);

const formatDateTime = (value) => {
  if (!value) return 'Not scheduled';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not scheduled';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  }).format(date);
};

const extractError = (error, fallback) => (
  error?.response?.error ||
  error?.response?.message ||
  error?.message ||
  error?.error ||
  fallback
);

const asArray = (value) => Array.isArray(value) ? value : [];

const humanizeDecisionText = (value) => {
  const text = String(value || '').trim();
  if (!text) return '';

  const yearsEvidence = text.match(/^Has (.+?) with ([\d.]+) years evidence\.?$/i);
  if (yearsEvidence) {
    const label = yearsEvidence[1];
    const years = Number(yearsEvidence[2]);
    if (!Number.isFinite(years) || years <= 0) return `${label} is present in evidence, but duration was not confirmed.`;
    return `${label} is supported by ${years} ${years === 1 ? 'year' : 'years'} of evidence.`;
  }

  const missingRequired = text.match(/^Missing required skill:\s*(.+)$/i);
  if (missingRequired) return `Verify required skill evidence: ${missingRequired[1]}.`;

  const missingPreferred = text.match(/^Missing preferred skill:\s*(.+)$/i);
  if (missingPreferred) return `Verify preferred skill evidence: ${missingPreferred[1]}.`;

  const missingEvidence = text.match(/^Missing required evidence:\s*(.+)$/i);
  if (missingEvidence) return `Verify required evidence: ${missingEvidence[1]}`;

  return text;
};

const factText = (item) => {
  if (!item) return '';
  if (typeof item === 'string') return humanizeDecisionText(item);
  return humanizeDecisionText(item.explanation || item.summary || item.label || item.name || item.key || item.title || JSON.stringify(item));
};

const compactList = (value, limit = 4) => (
  asArray(value)
    .filter(Boolean)
    .slice(0, limit)
    .map((item) => typeof item === 'string' ? labelize(item) : factText(item))
    .join(' · ')
);

const initialsFor = (name) => (
  String(name || 'Candidate')
    .split(' ')
    .filter(Boolean)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()
);

const scoreTone = (score) => {
  const numeric = Number(score) || 0;
  if (numeric >= 80) return 'border-emerald-100 bg-emerald-50 text-emerald-700';
  if (numeric >= 65) return 'border-sky-100 bg-sky-50 text-sky-700';
  if (numeric >= 50) return 'border-amber-100 bg-amber-50 text-amber-700';
  return 'border-slate-200 bg-slate-50 text-slate-600';
};

const scoreValue = (value, fallback = 0) => {
  if (value === null || value === undefined || value === '') return fallback;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return fallback;
  return Math.max(0, Math.min(100, numeric <= 1 ? numeric * 100 : numeric));
};

const percentText = (value) => {
  const numeric = scoreValue(value, null);
  return numeric === null ? null : `${Math.round(numeric)}%`;
};

const dimensionLabel = (dimension) => {
  const key = String(typeof dimension === 'string' ? dimension : dimension?.key || dimension?.label || '').toLowerCase();
  const labels = {
    skills: 'Skills Evidence',
    experience: 'Experience Evidence',
    education: 'Education Context',
    location: 'Location And Work Model',
    salary: 'Compensation Fit',
    micro_points: 'Role-Specific Checks',
    micro_point: 'Role-Specific Checks'
  };
  return labels[key] || labelize(typeof dimension === 'string' ? dimension : dimension?.label || dimension?.key);
};

const dimensionRowsFor = (match = {}) => {
  const insightRows = asArray(match.insights?.dimensions || match.insights?.topDimensions);
  if (insightRows.length) return insightRows;
  const dimensions = match.scoreBreakdown?.dimensions || match.scoreBreakdown || {};
  return Object.entries(dimensions)
    .filter(([, value]) => value !== null && value !== undefined && value !== '' && Number.isFinite(Number(value)))
    .map(([key, value]) => ({ key, label: labelize(key), score: scoreValue(value) }));
};

const riskRowsFor = (match = {}) => (
  asArray(match.insights?.reviewRisks || match.explanation?.gaps)
    .concat(asArray(match.missingSkills), asArray(match.missingQualifications))
    .filter(Boolean)
);

const strengthRowsFor = (match = {}) => (
  asArray(match.explanation?.strengths)
    .concat(asArray(match.insights?.requirementEvidence), asArray(match.matchingSkills))
    .filter(Boolean)
);

const evidenceCountsFor = (candidate = {}, match = {}) => {
  const profileDetails = candidate.profileDetails || {};
  const evidence = match.insights?.candidateEvidence || {};
  const dataDepth = match.insights?.dataDepth || {};
  const count = (key, fallback) => Number(dataDepth[key]) || asArray(profileDetails[key]).length || asArray(evidence[key]).length || asArray(fallback).length;

  return {
    skills: count('skills', candidate.skills),
    experience: count('experience', candidate.experience),
    education: count('education', candidate.education),
    certifications: count('certifications', candidate.certifications),
    projects: count('projects', candidate.projects),
    languages: count('languages', candidate.languages),
    profileAnswers: Number(dataDepth.profileAnswers) || asArray(profileDetails.profileAnswers).length || asArray(evidence.profileAnswerHighlights || candidate.profileAnswerHighlights).length,
    profileSignals: Number(dataDepth.profileSignals) || asArray(profileDetails.profileSignals).length || asArray(evidence.profileSignals || candidate.profileSignals).length,
    evidenceSignals: Number(dataDepth.evidenceSignals) || asArray(match.insights?.evidenceSignals || match.microPoints).length
  };
};

const totalEvidenceCount = (counts = {}) => Object.values(counts).reduce((sum, value) => sum + (Number(value) || 0), 0);

const MiniBar = ({ value }) => {
  const score = scoreValue(value, null);
  return (
    <div className="h-2 overflow-hidden rounded-full bg-slate-100">
      <div className="h-full rounded-full bg-[#0ea5e9]" style={{ width: score === null ? '0%' : `${Math.max(5, score)}%` }} />
    </div>
  );
};


const statusTone = (status) => {
  const normalized = String(status || '').toLowerCase();
  if (['mutual', 'interview_passed', 'assessment_passed', 'offer_accepted', 'hired'].includes(normalized)) return 'border-emerald-100 bg-emerald-50 text-emerald-700';
  if (['employer_interested', 'candidate_interested', 'interview_scheduled', 'assessment_completed'].includes(normalized)) return 'border-sky-100 bg-sky-50 text-sky-700';
  if (['potential', 'interview_needs_discussion', 'hold'].includes(normalized)) return 'border-amber-100 bg-amber-50 text-amber-700';
  if (['employer_rejected', 'interview_failed', 'candidate_declined', 'assessment_failed'].includes(normalized)) return 'border-rose-100 bg-rose-50 text-rose-700';
  return 'border-slate-200 bg-slate-50 text-slate-600';
};

const Panel = ({ title, icon: Icon, action, children, className = '' }) => (
  <section className={`overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm ${className}`}>
    <div className="border-b border-slate-100/80 bg-slate-50/50 px-5 py-4 sm:px-6">
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          {Icon && (
            <span className="flex shrink-0 items-center justify-center text-[#0b1f3a]">
              <Icon size={19} />
            </span>
          )}
          <h2 className="truncate font-display text-[17px] font-medium text-[#0b1f3a]">{title}</h2>
        </div>
        {action}
      </div>
    </div>
    <div className="p-5 sm:p-6">{children}</div>
  </section>
);

const Metric = ({ label, value, detail, icon: Icon }) => (
  <div className="min-w-0 rounded-2xl border border-slate-200/80 bg-gradient-to-b from-white to-slate-50/50 p-5 shadow-sm">
    <div className="mb-4 flex shrink-0 items-center justify-center text-sky-600">
      <Icon size={24} />
    </div>
    <div className="truncate font-display text-[26px] font-medium leading-none text-[#0b1f3a]">{value}</div>
    <div className="mt-1.5 truncate text-sm font-semibold text-slate-700">{label}</div>
    <div className="mt-1 truncate text-xs font-medium text-slate-500">{detail}</div>
  </div>
);

const EmptyState = ({ label }) => (
  <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
    {label}
  </div>
);

const Badge = ({ children, tone = 'slate' }) => {
  const tones = {
    slate: 'border-slate-200 bg-slate-50 text-slate-600',
    sky: 'border-sky-100 bg-sky-50 text-sky-700',
    green: 'border-emerald-100 bg-emerald-50 text-emerald-700',
    amber: 'border-amber-100 bg-amber-50 text-amber-700'
  };

  return (
    <span className={`inline-flex rounded-md border px-2.5 py-1 text-xs font-medium ${tones[tone] || tones.slate}`}>
      {children}
    </span>
  );
};

const Timeline = ({ interviews, assessments, offers }) => {
  const events = [
    ...asArray(interviews).map((item) => ({
      id: `interview-${item.id}`,
      type: 'Interview',
      title: item.interview_title || item.title || 'Interview',
      detail: `${labelize(item.interview_status || item.status)} · ${formatDateTime(item.scheduled_date || item.scheduledDate)}`,
      date: item.scheduled_date || item.scheduledDate || item.created_at
    })),
    ...asArray(assessments).map((item) => ({
      id: `assessment-${item.id}`,
      type: 'Assessment',
      title: item.assessment_title || item.assessment?.title || 'Assessment',
      detail: `${labelize(item.attempt_status || item.status)} · ${item.percentage_score ?? item.score ?? 'No'} score`,
      date: item.submitted_at || item.started_at || item.created_at
    })),
    ...asArray(offers).map((item) => ({
      id: `offer-${item.id}`,
      type: 'Offer',
      title: item.offer_title || item.job_title || 'Offer',
      detail: labelize(item.offer_status || item.status),
      date: item.created_at
    }))
  ].sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));

  if (!events.length) return <EmptyState label="No workflow events yet." />;

  return (
    <div className="space-y-3">
      {events.map((event) => (
        <div key={event.id} className="flex gap-3 rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
          <div className="mt-1 h-2 w-2 shrink-0 rounded-full bg-sky-500" />
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium text-[#0b1f3a]">{event.title}</span>
              <Badge tone="sky">{event.type}</Badge>
            </div>
            <div className="mt-1 text-xs text-slate-500">{event.detail}</div>
          </div>
        </div>
      ))}
    </div>
  );
};

const CandidateDossier = ({ candidate = {}, selectedMatch, matches = [] }) => {
  const counts = evidenceCountsFor(candidate, selectedMatch || {});
  const evidenceTotal = totalEvidenceCount(counts);
  const dimensions = dimensionRowsFor(selectedMatch).slice(0, 5);
  const preferences = selectedMatch?.insights?.candidateEvidence?.preferences || candidate.preferences || {};
  const snapshot = selectedMatch?.insights?.candidateSnapshot || {};
  const currentRole = selectedMatch?.insights?.candidateEvidence?.currentRole || candidate.currentRole || {};
  const salary = formatMoney(candidate.salaryExpectations);
  const profileCompletion = snapshot.profileCompletionPercentage ?? candidate.profile?.completionPercentage ?? candidate.profileCompletionPercentage;
  const riskCount = riskRowsFor(selectedMatch).length;
  const strongestMatch = [...matches].sort((a, b) => scoreValue(b.overallMatchScore) - scoreValue(a.overallMatchScore))[0];

  const numeric = Math.round(scoreValue(selectedMatch?.overallMatchScore || 0));
  const scoreToneClass = numeric >= 80 ? 'text-emerald-600' : numeric >= 65 ? 'text-sky-600' : numeric >= 50 ? 'text-amber-600' : 'text-slate-500';
  const scoreBgClass = numeric >= 80 ? 'bg-emerald-50 border-emerald-100' : numeric >= 65 ? 'bg-sky-50 border-sky-100' : numeric >= 50 ? 'bg-amber-50 border-amber-100' : 'bg-slate-50 border-slate-200';
  const scoreBarColor = numeric >= 80 ? '#059669' : numeric >= 65 ? '#0ea5e9' : numeric >= 50 ? '#d97706' : '#94a3b8';

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="flex items-center gap-2 border-b border-slate-100 px-5 py-3 text-xs font-semibold uppercase tracking-widest text-slate-500">
        <PiUserLight size={12} /> Candidate Dossier
      </div>
      <div className="grid lg:grid-cols-[280px_1fr_1fr]">

        {/* Score column — large typographic display, no squeezing */}
        <div className="flex min-w-0 flex-col justify-between gap-5 border-b border-slate-100 p-6 lg:border-b-0 lg:border-r">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-widest text-slate-400">Match Score</div>
            <div className="mt-2 flex items-end gap-3">
              <span className={`font-display text-[64px] font-bold leading-none ${scoreToneClass}`}>{numeric}</span>
              <span className="mb-2 text-2xl font-light text-slate-300">/ 100</span>
            </div>
            <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
              <div className="h-full rounded-full transition-all" style={{ width: `${numeric}%`, backgroundColor: scoreBarColor }} />
            </div>
            <div className="mt-2 text-xs text-slate-500">{selectedMatch?.job?.title || 'No role selected'}</div>
          </div>

          <div className={`rounded-lg border px-4 py-3 ${scoreBgClass}`}>
            <div className="grid grid-cols-2 gap-x-4 gap-y-3">
              <div className="min-w-0">
                <div className="truncate text-[10px] font-semibold uppercase tracking-wider text-slate-400">Best Fit</div>
                <div className="mt-0.5 truncate text-xs font-semibold text-[#0b1f3a]">{strongestMatch?.job?.title || selectedMatch?.job?.title || '—'}</div>
              </div>
              <div className="min-w-0">
                <div className="truncate text-[10px] font-semibold uppercase tracking-wider text-slate-400">Evidence</div>
                <div className="mt-0.5 truncate text-xs font-semibold text-[#0b1f3a]">{formatNumber(evidenceTotal)} signals</div>
              </div>
              <div className="min-w-0">
                <div className="truncate text-[10px] font-semibold uppercase tracking-wider text-slate-400">Onboarding</div>
                <div className="mt-0.5 truncate text-xs font-semibold text-[#0b1f3a]">
                  {profileCompletion !== null && profileCompletion !== undefined ? percentText(profileCompletion) : '—'}
                </div>
              </div>
              <div className="min-w-0">
                <div className="truncate text-[10px] font-semibold uppercase tracking-wider text-slate-400">Risk Flags</div>
                <div className="mt-0.5 truncate text-xs font-semibold text-[#0b1f3a]">{riskCount ? `${riskCount} flagged` : 'Clear'}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Logistics column */}
        <div className="min-w-0 border-b border-slate-100 p-6 lg:border-b-0 lg:border-r">
          <div className="mb-4 text-[11px] font-bold uppercase tracking-widest text-slate-400">Logistics</div>
          <dl className="space-y-3 text-sm">
            {[
              ['Current Role', currentRole.job_title || currentRole.jobTitle],
              ['Experience', candidate.experienceYears !== null && candidate.experienceYears !== undefined ? `${candidate.experienceYears} yrs` : labelize(candidate.experienceLevel)],
              ['Location', candidate.location],
              ['Expected Pay', salary],
              ['Availability', candidate.availability ? labelize(candidate.availability) : null],
              ['Work Mode', preferences.preferredWorkType ? labelize(preferences.preferredWorkType) : null],
              ['Authorization', preferences.workAuthorizationStatus ? labelize(preferences.workAuthorizationStatus) : null],
              ['Notice Period', preferences.noticePeriodDays !== null && preferences.noticePeriodDays !== undefined ? `${preferences.noticePeriodDays} days` : null],
            ].filter(([, v]) => v).map(([k, v]) => (
              <div key={k} className="flex items-baseline justify-between gap-4 border-b border-slate-50 pb-3 last:border-0 last:pb-0">
                <dt className="shrink-0 text-slate-500">{k}</dt>
                <dd className="min-w-0 truncate text-right font-semibold text-[#0b1f3a]">{v}</dd>
              </div>
            ))}
            {compactList(preferences.preferredIndustries) && (
              <div className="flex items-baseline justify-between gap-4 border-b border-slate-50 pb-3 last:border-0 last:pb-0">
                <dt className="shrink-0 text-slate-500">Industries</dt>
                <dd className="min-w-0 truncate text-right font-semibold text-[#0b1f3a]">{compactList(preferences.preferredIndustries)}</dd>
              </div>
            )}
          </dl>
        </div>

        {/* Signals + dimensions column */}
        <div className="min-w-0 p-6">
          <div className="mb-4 text-[11px] font-bold uppercase tracking-widest text-slate-400">Evidence Signals</div>
          <ul className="space-y-3 text-sm">
            {[
              ['Skills', counts.skills, PiTargetLight, 'text-sky-600'],
              ['Experience', counts.experience, PiBriefcaseLight, 'text-amber-600'],
              ['Education', counts.education, PiBookOpenLight, 'text-emerald-600'],
              ['Projects', counts.projects, PiStackLight, 'text-purple-600'],
              ['Certifications', counts.certifications, PiMedalLight, 'text-rose-600'],
              ['Evidence Checks', counts.evidenceSignals, PiGaugeLight, 'text-slate-500'],
            ].map(([label, count, IconComp, colorClass]) => (
              <li key={label} className="flex items-center justify-between gap-4 border-b border-slate-50 pb-3 last:border-0 last:pb-0">
                <div className="flex items-center gap-2.5 text-slate-500">
                  <IconComp size={12} className={colorClass} />
                  <span>{label}</span>
                </div>
                <span className="font-semibold text-[#0b1f3a]">{formatNumber(count)}</span>
              </li>
            ))}
          </ul>
          {dimensions.length > 0 && (
            <div className="mt-5 space-y-3">
              <div className="text-[11px] font-bold uppercase tracking-widest text-slate-400">Fit Dimensions</div>
              {dimensions.map((dimension) => (
                <div key={dimension.key || dimension.label}>
                  <div className="mb-1.5 flex items-center justify-between gap-2">
                    <span className="min-w-0 truncate text-xs text-slate-600">{dimensionLabel(dimension)}</span>
                    <span className="shrink-0 text-xs font-bold text-[#0b1f3a]">{percentText(dimension.score) || 'Not scored'}</span>
                  </div>
                  <MiniBar value={dimension.score} />
                </div>
              ))}
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

const RoleFitComparison = ({ matches = [], selectedMatchId, onSelect }) => {
  if (matches.length <= 1) return null;
  const rows = [...matches].sort((a, b) => scoreValue(b.overallMatchScore) - scoreValue(a.overallMatchScore));

  return (
    <Panel title="Role Fit Comparison" icon={PiChartLineUpLight}>
      <div className="grid gap-3 lg:grid-cols-2">
        {rows.map((match) => {
          const selected = String(match.matchId) === String(selectedMatchId);
          return (
            <button
              key={match.matchId}
              onClick={() => onSelect(String(match.matchId))}
              className={`rounded-xl border p-4 text-left transition ${selected ? 'border-sky-300 bg-sky-50 shadow-sm' : 'border-slate-200 bg-white hover:border-sky-200 hover:bg-slate-50'}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="truncate text-sm font-semibold text-[#0b1f3a]">{match.job?.title || 'Untitled role'}</div>
                  <div className="mt-1 flex flex-wrap gap-1.5 text-[11px] text-slate-500">
                    <span>Band {match.matchBand || '-'}</span>
                    <span>{labelize(match.matchStatus)}</span>
                    {match.employerAction && <span>Employer {labelize(match.employerAction)}</span>}
                    {match.candidateAction && <span>Candidate {labelize(match.candidateAction)}</span>}
                  </div>
                </div>
                <span className={`shrink-0 rounded-md border px-2 py-1 text-xs font-semibold ${scoreTone(match.overallMatchScore)}`}>{Math.round(scoreValue(match.overallMatchScore))}%</span>
              </div>
              <div className="mt-3">
                <MiniBar value={match.overallMatchScore} />
              </div>
            </button>
          );
        })}
      </div>
    </Panel>
  );
};

const DecisionBriefPanel = ({ match }) => {
  if (!match) return null;
  const strengths = strengthRowsFor(match).slice(0, 5);
  const risks = riskRowsFor(match).slice(0, 5);
  const dimensions = dimensionRowsFor(match).slice(0, 4);

  return (
    <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_340px]">
      <div className="flex flex-col overflow-hidden rounded-2xl border border-emerald-200/60 bg-gradient-to-b from-emerald-50/50 to-emerald-50/10 shadow-sm">
        <div className="flex items-center gap-2 border-b border-emerald-100 bg-emerald-50/50 px-5 py-4 text-xs font-bold uppercase tracking-widest text-emerald-800">
          <PiCheckCircleLight size={12} /> Advance Reasons
        </div>
        <div className="p-5">
          {strengths.length ? (
            <ul className="space-y-3 text-sm leading-6 text-emerald-950/80">
              {strengths.map((item, index) => (
                <li key={index} className="flex gap-3">
                   <div className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-400" />
                   <span>{factText(item)}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm font-medium leading-6 text-emerald-800/60">No explicit strengths were returned with this match.</p>
          )}
        </div>
      </div>

      <div className="flex flex-col overflow-hidden rounded-2xl border border-amber-200/60 bg-gradient-to-b from-amber-50/50 to-amber-50/10 shadow-sm">
        <div className="flex items-center gap-2 border-b border-amber-100 bg-amber-50/50 px-5 py-4 text-xs font-bold uppercase tracking-widest text-amber-800">
          <PiShieldCheckLight size={12} /> Verification Focus
        </div>
        <div className="p-5">
          {risks.length ? (
            <ul className="space-y-3 text-sm leading-6 text-amber-950/80">
              {risks.map((item, index) => (
                <li key={index} className="flex gap-3">
                   <div className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-400" />
                   <span>{factText(item)}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm font-medium leading-6 text-amber-800/60">No material review gaps flagged.</p>
          )}
        </div>
      </div>

      <div className="flex flex-col overflow-hidden rounded-2xl border border-slate-200/60 bg-gradient-to-b from-slate-50/80 to-white shadow-sm">
        <div className="flex items-center gap-2 border-b border-slate-100 bg-slate-50/50 px-5 py-4 text-xs font-bold uppercase tracking-widest text-slate-600">
          <PiTargetLight size={12} /> Fit Snapshot
        </div>
        <div className="flex flex-col justify-center space-y-4 p-5">
          {dimensions.map((dimension) => (
            <div key={dimension.key || dimension.label}>
              <div className="mb-1.5 flex items-center justify-between gap-3">
                <span className="min-w-0 truncate text-xs font-medium text-slate-600">{dimensionLabel(dimension)}</span>
                <span className="text-xs font-bold text-[#0b1f3a]">{percentText(dimension.score) || 'Not scored'}</span>
              </div>
              <MiniBar value={dimension.score} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const CandidateProfile = () => {
  const { candidateId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const queryMatchId = params.get('matchId');
  const queryJobId = params.get('jobId');
  const [candidate, setCandidate] = useState(null);
  const [summary, setSummary] = useState(null);
  const [selectedMatchId, setSelectedMatchId] = useState(queryMatchId || '');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionKey, setActionKey] = useState('');

  const loadCandidate = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await candidateAPI.getEmployerCandidate(candidateId, { limit: 100, minScore: 0 });
      let nextCandidate = response.candidate || null;
      if (queryJobId && queryMatchId) {
        try {
          const matchResponse = await jobAPI.getMatchDetails(queryJobId, queryMatchId);
          const detailedMatch = matchResponse.match || matchResponse;
          if (detailedMatch?.matchId) {
            const existingMatches = asArray(nextCandidate?.matches);
            nextCandidate = {
              ...(nextCandidate || {}),
              ...(detailedMatch.candidate || {}),
              profileDetails: detailedMatch.candidate?.profileDetails || nextCandidate?.profileDetails,
              matches: [
                detailedMatch,
                ...existingMatches.filter((match) => String(match.matchId) !== String(detailedMatch.matchId))
              ]
            };
          }
        } catch (_) {
          // Keep the candidate page usable if a direct match refresh is unavailable.
        }
      }
      setCandidate(nextCandidate);
      setSummary(response.summary || null);
      setSelectedMatchId((current) => current || String(nextCandidate?.matches?.[0]?.matchId || ''));
    } catch (loadError) {
      setError(extractError(loadError, 'Failed to load candidate profile'));
    } finally {
      setLoading(false);
    }
  }, [candidateId, queryJobId, queryMatchId]);

  useEffect(() => {
    loadCandidate();
  }, [loadCandidate]);

  const matches = asArray(candidate?.matches);
  const selectedMatch = useMemo(() => (
    matches.find((match) => String(match.matchId) === String(selectedMatchId)) || matches[0] || null
  ), [matches, selectedMatchId]);

  const fullName = candidate?.fullName || 'Candidate';
  const canSchedule = selectedMatch?.employerAction === 'interested' && selectedMatch?.candidateAction === 'interested';
  const score = Math.round(Number(selectedMatch?.overallMatchScore || 0));

  const handleAction = async (action) => {
    if (!selectedMatch) return;
    setActionKey(action);
    try {
      await jobAPI.takeMatchAction(selectedMatch.jobId, selectedMatch.matchId, {
        action,
        notifyCandidate: action === 'interested'
      });
      toast.success(action === 'interested' ? 'Interest recorded and candidate notified.' : 'Candidate marked as passed.');
      loadCandidate();
    } catch (actionError) {
      toast.error(extractError(actionError, 'Could not update match'));
    } finally {
      setActionKey('');
    }
  };

  if (loading && !candidate) {
    return <CandidateProfileSkeleton />;
  }

  return (
    <div className="min-h-screen bg-[#f5f6f8] p-4 sm:p-6 lg:p-8">
      <div className="mx-auto max-w-[1500px] space-y-5">
        <div>
          <nav className="mb-3 flex items-center gap-1.5 text-sm text-slate-500">
            <button onClick={() => navigate('/candidates')} className="inline-flex items-center gap-1 font-medium transition-colors hover:text-[#0b1f3a]">
              <PiArrowLeftLight size={12} /> Pipeline
            </button>
            <PiCaretRightLight size={12} className="opacity-40" />
            <span className="font-semibold text-[#0b1f3a]">{fullName}</span>
          </nav>

          <div className="flex flex-col gap-4 rounded-xl border border-slate-200 bg-white px-6 py-5 shadow-sm lg:flex-row lg:items-center lg:justify-between">
            <div className="flex min-w-0 items-center gap-4">
              <div className="relative shrink-0">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#0b1f3a] text-xl font-bold text-white">
                  {initialsFor(fullName)}
                </div>
                {score > 0 && (
                  <span className={`absolute -bottom-1 -right-1 flex h-7 w-7 items-center justify-center rounded-full border-2 border-white text-[10px] font-bold ${score >= 80 ? 'bg-emerald-500 text-white' : score >= 65 ? 'bg-sky-500 text-white' : score >= 50 ? 'bg-amber-500 text-white' : 'bg-slate-400 text-white'}`}>
                    {score}
                  </span>
                )}
              </div>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="font-display text-2xl font-semibold text-[#0b1f3a]">{fullName}</h1>
                  {candidate?.availability && (
                    <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">{labelize(candidate.availability)}</span>
                  )}
                  {selectedMatch?.matchStatus && (
                    <span className={`rounded-full border px-2 py-0.5 text-xs font-medium ${statusTone(selectedMatch.matchStatus)}`}>{labelize(selectedMatch.matchStatus)}</span>
                  )}
                </div>
                <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-500">
                  {candidate?.location && <span className="inline-flex items-center gap-1"><PiMapPinLight size={12} /> {candidate.location}</span>}
                  {candidate?.experienceYears !== null && candidate?.experienceYears !== undefined && <span className="inline-flex items-center gap-1"><PiBriefcaseLight size={12} /> {candidate.experienceYears} yrs experience</span>}
                  {candidate?.experienceLevel && <span>{labelize(candidate.experienceLevel)}</span>}
                  {selectedMatch?.job?.title && <span className="inline-flex items-center gap-1 font-medium text-sky-700"><PiTargetLight size={12} /> Matched to: {selectedMatch.job.title}</span>}
                </div>
              </div>
            </div>
            <div className="flex shrink-0 flex-wrap gap-2">
              <button
                onClick={() => handleAction('interested')}
                disabled={!selectedMatch || actionKey === 'interested' || selectedMatch?.employerAction === 'interested'}
                className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-[#0b1f3a] px-4 text-sm font-semibold text-white transition hover:bg-[#162d52] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {actionKey === 'interested' ? <PiCircleNotchLight className="animate-spin" size={12} /> : <PiCheckCircleLight size={12} />} Express Interest
              </button>
              <button
                onClick={() => { if (selectedMatch) navigate(`/interviews/schedule?jobId=${selectedMatch.jobId}&matchId=${selectedMatch.matchId}&candidateId=${candidateId}`); }}
                disabled={!canSchedule}
                className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <PiCalendarCheckLight size={12} /> Schedule
              </button>
              <button
                onClick={() => handleAction('not_interested')}
                disabled={!selectedMatch || actionKey === 'not_interested' || ['rejected', 'not_interested'].includes(selectedMatch?.employerAction)}
                className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-700 transition hover:border-rose-200 hover:bg-rose-50 hover:text-rose-700 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {actionKey === 'not_interested' ? <PiCircleNotchLight className="animate-spin" size={12} /> : <PiThumbsDownLight size={12} />} Pass
              </button>
            </div>
          </div>
        </div>

        {error && (
          <div className="flex items-start gap-2 rounded-lg border border-rose-100 bg-rose-50 p-4 text-sm text-rose-700">
            <PiWarningCircleLight size={14} className="mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {selectedMatch && (
          <CandidateDossier candidate={candidate} selectedMatch={selectedMatch} matches={matches} />
        )}

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
          <Metric icon={PiBriefcaseLight} label="Job matches" value={formatNumber(summary?.totalMatches || matches.length)} detail="Visible to this employer" />
          <Metric icon={PiShieldCheckLight} label="Average score" value={`${summary?.averageScore || 0}%`} detail="Across matched jobs" />
          <Metric icon={PiCheckCircleLight} label="Mutual interest" value={formatNumber(summary?.mutualMatches || 0)} detail="Accepted both ways" />
          <Metric icon={PiCalendarCheckLight} label="Interviews" value={formatNumber(candidate?.interviews?.length || 0)} detail="All statuses" />
          <Metric icon={PiFileTextLight} label="Assessments" value={formatNumber(candidate?.assessmentAttempts?.length || 0)} detail="Assigned attempts" />
        </div>

        <div className="space-y-6">
          <RoleFitComparison
            matches={matches}
            selectedMatchId={selectedMatch?.matchId}
            onSelect={setSelectedMatchId}
          />

          <Panel
            title="Match Context"
            icon={PiGaugeLight}
            action={matches.length > 1 && (
              <select
                value={selectedMatch?.matchId || ''}
                onChange={(event) => setSelectedMatchId(event.target.value)}
                className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-xs text-slate-700 outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
              >
                {matches.map((match) => (
                  <option key={match.matchId} value={match.matchId}>
                    {match.job?.title || 'Job'} · {Math.round(Number(match.overallMatchScore || 0))}%
                  </option>
                ))}
              </select>
            )}
          >
            {selectedMatch ? (
              <div className="space-y-5">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                  <div>
                    <h2 className="font-display text-xl font-normal text-[#0b1f3a]">{selectedMatch.job?.title || 'Untitled role'}</h2>
                    <div className="mt-2 flex flex-wrap gap-2">
                      <span className={`rounded-md border px-2.5 py-1 text-xs font-medium ${scoreTone(score)}`}>Band {selectedMatch.matchBand || '-'} · {score}%</span>
                      <span className={`rounded-md border px-2.5 py-1 text-xs font-medium ${statusTone(selectedMatch.matchStatus)}`}>{labelize(selectedMatch.matchStatus)}</span>
                      {selectedMatch.employerAction && <Badge tone="sky">Employer {labelize(selectedMatch.employerAction)}</Badge>}
                      {selectedMatch.candidateAction && <Badge tone="green">Candidate {labelize(selectedMatch.candidateAction)}</Badge>}
                    </div>
                  </div>
                  <button
                    onClick={() => navigate(`/jobs/${selectedMatch.jobId}/matches`)}
                    className="inline-flex h-9 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-xs font-medium text-slate-600 transition hover:bg-slate-50"
                  >
                    Job matches <PiArrowSquareOutLight size={12} />
                  </button>
                </div>

                <DecisionBriefPanel match={selectedMatch} />

                <MatchIntelligencePanel match={selectedMatch} candidate={candidate} />
              </div>
            ) : (
              <EmptyState label="No match context found for this candidate." />
            )}
          </Panel>

          <Panel title="Workflow Timeline" icon={PiCalendarCheckLight}>
            <Timeline
              interviews={candidate?.interviews}
              assessments={candidate?.assessmentAttempts}
              offers={candidate?.offers}
            />
          </Panel>
        </div>
      </div>
    </div>
  );
};

export default CandidateProfile;
